import React, { useState } from 'react';
import { FiCalendar, FiUser, FiPhone, FiMail, FiMapPin, FiUsers, FiTruck, FiClock } from 'react-icons/fi';
import './ReservationForm.css';

const defaultFormData = {
  nom: '',
  prenom: '',
  telephone: '',
  email: '',
  dateDepart: '',
  heureDepart: '',
  dateRetour: '',
  heureRetour: '',
  evenement: '',
  nombrePersonnes: '',
  typeVehicule: '',
  lieuEmbarquement: '',
  destination: '',
  avecChauffeur: 'oui',
  message: ''
};

const ReservationForm = ({ prefill = null, onSuccess }) => {
  const [formData, setFormData] = useState({ ...defaultFormData, ...(prefill || {}) });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const evenements = [
    'Mariage',
    'Demande en mariage',
    'Dîner d\'affaires',
    'Anniversaire',
    'Soirée de gala',
    'Transfert aéroport',
    'Tour de ville',
    'Sortie en groupe',
    'Événement corporatif',
    'Autre'
  ];

  const typesVehicules = [
    'Limousine classique',
    'Limousine stretch',
    'Mercedes Classe S',
    'BMW Série 7',
    'Audi A8',
    'Rolls Royce',
    'Bentley',
    'Range Rover',
    'Van de luxe'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const phoneNumber = '33661630113';

    const formatDate = (d) => {
      if (!d) return '';
      const [y, m, day] = d.split('-');
      return `${day}/${m}/${y}`;
    };

    const retourLine =
      formData.dateRetour || formData.heureRetour
        ? `🔁 *Retour* : ${formatDate(formData.dateRetour)}${
            formData.heureRetour ? ' à ' + formData.heureRetour : ''
          }\n`
        : '';

    const messageLine = formData.message
      ? `\n💬 *Message / Demandes spéciales*\n${formData.message}\n`
      : '';

    const message = `🚗 *Nouvelle demande de réservation*
_VIP Limousine Paris_

👤 *Contact*
• Nom : ${formData.nom}
• Prénom : ${formData.prenom}
• Téléphone : ${formData.telephone}
• Email : ${formData.email}

📅 *Date & heure de départ*
${formatDate(formData.dateDepart)} à ${formData.heureDepart}
${retourLine}
🎉 *Événement* : ${formData.evenement}
👥 *Nombre de personnes* : ${formData.nombrePersonnes}

🚘 *Véhicule souhaité* : ${formData.typeVehicule}
🧑‍✈️ *Avec chauffeur* : ${formData.avecChauffeur === 'oui' ? 'Oui' : 'Non'}

📍 *Itinéraire*
• Embarquement : ${formData.lieuEmbarquement}
• Destination : ${formData.destination}
${messageLine}
Merci de me confirmer la disponibilité et le tarif. 🙏`;

    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    setIsSubmitted(true);

    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ ...defaultFormData, ...(prefill || {}) });
      if (onSuccess) onSuccess();
    }, 3500);
  };

  if (isSubmitted) {
    return (
      <div className="form-success">
        <div className="success-icon">✓</div>
        <h3>WhatsApp ouvert !</h3>
        <p>Votre demande a été pré-remplie dans WhatsApp. Il ne vous reste plus qu'à cliquer sur <strong>Envoyer</strong>.</p>
        <p className="success-note">Notre équipe vous répondra dans les plus brefs délais pour confirmer votre réservation.</p>
      </div>
    );
  }

  return (
    <form className="reservation-form" onSubmit={handleSubmit}>
      <h2 className="form-title">Réservation</h2>
      <p className="form-subtitle">Réservez votre transport de luxe en quelques clics</p>
      
      {/* Informations personnelles */}
      <div className="form-section">
        <h3 className="section-title">
          <FiUser />
          Informations personnelles
        </h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="nom">Nom *</label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              required
              placeholder="Votre nom"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="prenom">Prénom *</label>
            <input
              type="text"
              id="prenom"
              name="prenom"
              value={formData.prenom}
              onChange={handleInputChange}
              required
              placeholder="Votre prénom"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="telephone">
              <FiPhone />
              Téléphone *
            </label>
            <input
              type="tel"
              id="telephone"
              name="telephone"
              value={formData.telephone}
              onChange={handleInputChange}
              required
              placeholder="+33 1 23 45 67 89"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">
              <FiMail />
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="votre@email.com"
            />
          </div>
        </div>
      </div>

      {/* Détails de la réservation */}
      <div className="form-section">
        <h3 className="section-title">
          <FiCalendar />
          Détails de la réservation
        </h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="dateDepart">Date de départ *</label>
            <input
              type="date"
              id="dateDepart"
              name="dateDepart"
              value={formData.dateDepart}
              onChange={handleInputChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="heureDepart">
              <FiClock />
              Heure de départ *
            </label>
            <input
              type="time"
              id="heureDepart"
              name="heureDepart"
              value={formData.heureDepart}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="dateRetour">Date de retour</label>
            <input
              type="date"
              id="dateRetour"
              name="dateRetour"
              value={formData.dateRetour}
              onChange={handleInputChange}
              placeholder="Optionnel"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="heureRetour">Heure de retour</label>
            <input
              type="time"
              id="heureRetour"
              name="heureRetour"
              value={formData.heureRetour}
              onChange={handleInputChange}
              placeholder="Optionnel"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="evenement">Type d'événement *</label>
            <select
              id="evenement"
              name="evenement"
              value={formData.evenement}
              onChange={handleInputChange}
              required
            >
              <option value="">Sélectionnez un événement</option>
              {evenements.map((event, index) => (
                <option key={index} value={event}>
                  {event}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="nombrePersonnes">
              <FiUsers />
              Nombre de personnes *
            </label>
            <input
              type="number"
              id="nombrePersonnes"
              name="nombrePersonnes"
              value={formData.nombrePersonnes}
              onChange={handleInputChange}
              required
              min="1"
              max="20"
              placeholder="Ex: 4"
            />
          </div>
        </div>
      </div>

      {/* Véhicule et service */}
      <div className="form-section">
        <h3 className="section-title">
          <FiTruck />
          Véhicule et service
        </h3>
        
        <div className="form-group">
          <label htmlFor="typeVehicule">Type de véhicule *</label>
          <select
            id="typeVehicule"
            name="typeVehicule"
            value={formData.typeVehicule}
            onChange={handleInputChange}
            required
          >
            <option value="">Sélectionnez un véhicule</option>
            {typesVehicules.map((vehicle, index) => (
              <option key={index} value={vehicle}>
                {vehicle}
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label>Avec chauffeur professionnel *</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="avecChauffeur"
                value="oui"
                checked={formData.avecChauffeur === 'oui'}
                onChange={handleInputChange}
              />
              <span>Oui (recommandé)</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="avecChauffeur"
                value="non"
                checked={formData.avecChauffeur === 'non'}
                onChange={handleInputChange}
              />
              <span>Non (location simple)</span>
            </label>
          </div>
        </div>
      </div>

      {/* Lieux */}
      <div className="form-section">
        <h3 className="section-title">
          <FiMapPin />
          Lieux de prise en charge et destination
        </h3>
        
        <div className="form-group">
          <label htmlFor="lieuEmbarquement">Lieu d'embarquement *</label>
          <input
            type="text"
            id="lieuEmbarquement"
            name="lieuEmbarquement"
            value={formData.lieuEmbarquement}
            onChange={handleInputChange}
            required
            placeholder="Adresse complète de départ"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="destination">Destination *</label>
          <input
            type="text"
            id="destination"
            name="destination"
            value={formData.destination}
            onChange={handleInputChange}
            required
            placeholder="Adresse de destination"
          />
        </div>
      </div>

      {/* Message */}
      <div className="form-section">
        <div className="form-group">
          <label htmlFor="message">Message ou demandes spéciales</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows="4"
            placeholder="Informations complémentaires, demandes spéciales..."
          />
        </div>
      </div>

      <button type="submit" className="btn btn-primary form-submit">
        Envoyer ma demande via WhatsApp
      </button>
    </form>
  );
};

export default ReservationForm;
